import type {InputProps} from '@yandex-delivery/blocks'
import {Input} from '@yandex-delivery/blocks'
import {debounce} from '@yandex-delivery/utils'
import clsx from 'clsx'
import {type ReactElement, useCallback, useEffect, useState} from 'react'
import {useController, useFormContext} from 'react-hook-form'

import * as classes from './Input.classes'

const DEBOUNCE_DELAY = 500

export type InputType = Omit<InputProps, 'name'> & {
  name: string
  shouldDebounce?: boolean
  inputTransformationFunction?: (arg0: string) => string
  debounceDelay?: number
}

export const TextInput = ({
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  defaultValue,
  name,
  shouldDebounce = true,
  onChange,
  debounceDelay = DEBOUNCE_DELAY,
  inputTransformationFunction,
  value: propValue,
  ...props
}: InputType): ReactElement => {
  const {control, clearErrors, watch} = useFormContext()
  const {
    field: {onChange: fieldOnChange, ...rest},
  } = useController({control, name})
  const stateValue = watch(name)
  const value = propValue ?? stateValue ?? ''
  const [debouncedValue, setDebouncedValue] = useState(value)
  const onChangeFunction = onChange ?? fieldOnChange

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFormStateUpdate = useCallback(
    debounce(e => {
      onChangeFunction(e)
    }, debounceDelay),
    [],
  )

  const handleDebouncedChange = (e: string): void => {
    const transformedInput = inputTransformationFunction ? inputTransformationFunction(e) : e
    setDebouncedValue(transformedInput)
    debouncedFormStateUpdate(transformedInput)
  }

  useEffect(() => {
    /** форсим ререндер при удалении точки */
    setDebouncedValue(value)
  }, [value])

  return (
    <Input
      {...rest}
      {...props}
      className={clsx(className, {[classes.hidden]: props.type === 'hidden'})}
      onChange={shouldDebounce ? handleDebouncedChange : onChangeFunction}
      onClick={() => {
        clearErrors(name)
      }}
      value={shouldDebounce ? debouncedValue : value}
    />
  )
}
